import React from 'react';
import { DataTable, toast, Button as DsButton } from '../../../components/ds';
import { Grid, Checkbox, TextField, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import SaveIcon from '@material-ui/icons/Save';

import AppState from '../../../state';
import { Utils } from '../../../services';

const CONFIRM_RATEIO_ENDPOINT = 'rateio/ConfirmaRateio.php';
const LIST_CONFIRM_RATEIO_ENDPOINT = 'rateio/GetLancamentosPendentesRateio.php';
const EDIT_REFERENCIA_ENDPOINT = 'rateio/EditReferenciaLancamento.php';

class ConfirmRateio extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            lancamentos: [],
            bloqueados: [],
            selectedById: {},
            referenciaById: {},
            loading: false,
            confirmDialogOpen: false,
            editDialogOpen: false,
            editLancamento: null,
            editReferenciaDraft: '',
            dataInicio: '',
            dataFim: '',
        };
    }

    componentDidMount() {
        this.getLancamentos();
    }

    normalizeDate = (value) => {
        if (!value) return '';
        const asString = String(value).trim();
        if (!asString) return '';

        if (asString.indexOf('/') > -1) {
            return Utils.dateToEN(asString);
        }

        return asString.slice(0, 10);
    };

    toMonthKey = (dateValue) => {
        const normalizedDate = this.normalizeDate(dateValue);
        if (!normalizedDate || normalizedDate.length < 7) return '';
        return normalizedDate.slice(0, 7);
    };

    getMonthLabel = (monthKey) => {
        if (!monthKey || monthKey.length !== 7) return '-';

        const [year, month] = monthKey.split('-');
        const monthNames = [
            'janeiro',
            'fevereiro',
            'março',
            'abril',
            'maio',
            'junho',
            'julho',
            'agosto',
            'setembro',
            'outubro',
            'novembro',
            'dezembro',
        ];

        const monthIndex = parseInt(month, 10) - 1;
        if (monthIndex < 0 || monthIndex > 11) return '-';
        return `${monthNames[monthIndex]}/${year}`;
    };

    toNumber = (value) => {
        if (value === null || value === undefined) return 0;
        if (typeof value === 'number') return value;

        const raw = String(value).trim();
        if (!raw) return 0;

        const normalized = raw.replace(/\./g, '').replace(',', '.');
        const parsed = Number(normalized);
        return Number.isNaN(parsed) ? 0 : parsed;
    };

    mapListResponse = (rawList = []) => {
        return rawList.map((item) => {
            const referencia =
                this.normalizeDate(item.referencia) ||
                this.normalizeDate(item.dt_referencia) ||
                this.normalizeDate(item.dt_pag_recb);

            return {
                ...item,
                referencia,
            };
        });
    };

    mapBlockedResponse = (rawList = []) => {
        return rawList.map((item) => ({
            ...item,
            referencia:
                this.normalizeDate(item.referencia) ||
                this.normalizeDate(item.dt_referencia) ||
                this.normalizeDate(item.dt_pag_recb),
        }));
    };

    getBackendErrorMessage = (error, defaultMessage) => {
        if (error && error.response && error.response.status && error.response.status.value) {
            return error.response.status.value;
        }

        if (error && error.rawText) {
            return error.rawText;
        }

        return defaultMessage;
    };

    parseErrorResponse = (response) => {
        return response.text().then((rawText) => {
            let parsed = null;

            try {
                parsed = rawText ? JSON.parse(rawText) : null;
            } catch (e) {
                parsed = null;
            }

            const error = new Error(String(response.status));
            error.status = response.status;
            error.response = parsed;
            error.rawText = rawText;
            throw error;
        });
    };

    getLancamentos = () => {
        this.setState({ loading: true });
        AppState.isLoading = true;

        const header = {
            'Content-Type': 'application/json; charset=utf-8',
            Authorization: AppState.token,
        };

        fetch(`${AppState.serverAddr}/${LIST_CONFIRM_RATEIO_ENDPOINT}`, {
            method: 'GET',
            headers: header,
        })
            .then((response) => {
                if (!response.ok) {
                    return this.parseErrorResponse(response);
                }

                return response.json();
            })
            .then((response) => {
                const request = response.request || {};
                const lancamentos = this.mapListResponse(request.elegiveis || []);
                const bloqueados = this.mapBlockedResponse(request.bloqueados || []);
                this.setInitialStateFromList(lancamentos, bloqueados);
            })
            .catch((error) => {
                const status = String(error.status || error.message || '');

                if (status === '401') {
                    window.location.reload();
                    return;
                }

                const backendMessage = this.getBackendErrorMessage(
                    error,
                    `HTTP ${status || 'desconhecido'}`
                );
                toast.error(`Erro ao carregar lançamentos para confirmação de rateio.\nDetalhe: ${backendMessage}`);

                this.setState({ loading: false });
                AppState.isLoading = false;
            });
    };

    setInitialStateFromList = (lancamentos, bloqueados = []) => {
        const referenciaById = {};

        lancamentos.forEach((item) => {
            referenciaById[item.id_lancamento] = this.normalizeDate(item.referencia);
        });

        bloqueados.forEach((item) => {
            referenciaById[item.id_lancamento] = this.normalizeDate(item.referencia);
        });

        this.setState({
            lancamentos,
            bloqueados,
            referenciaById,
            selectedById: {},
            loading: false,
        });
        AppState.isLoading = false;
    };

    handleSelect = (idLancamento) => (event) => {
        const checked = event.target.checked;

        this.setState((prevState) => ({
            selectedById: {
                ...prevState.selectedById,
                [idLancamento]: checked,
            },
        }));
    };

    openEditReference = (idLancamento) => {
        const lancamento = [...this.state.lancamentos, ...this.state.bloqueados].find(
            (item) => item.id_lancamento === idLancamento
        );

        if (!lancamento) {
            return;
        }

        const referenciaAtual =
            this.state.referenciaById[idLancamento] || lancamento.referencia || '';

        this.setState({
            editDialogOpen: true,
            editLancamento: lancamento,
            editReferenciaDraft: this.normalizeDate(referenciaAtual),
        });
    };

    closeEditReference = () => {
        this.setState({
            editDialogOpen: false,
            editLancamento: null,
            editReferenciaDraft: '',
        });
    };

    handleEditReferenceDraftChange = (event) => {
        this.setState({
            editReferenciaDraft: this.normalizeDate(event.target.value),
        });
    };

    handleDataInicioChange = (event) => {
        this.setState({
            dataInicio: this.normalizeDate(event.target.value),
        });
    };

    handleDataFimChange = (event) => {
        this.setState({
            dataFim: this.normalizeDate(event.target.value),
        });
    };

    isDateInRange = (dateValue) => {
        if (!dateValue) return true;

        const normalizedDate = this.normalizeDate(dateValue);
        const { dataInicio, dataFim } = this.state;

        if (dataInicio && normalizedDate < dataInicio) {
            return false;
        }

        if (dataFim && normalizedDate > dataFim) {
            return false;
        }

        return true;
    };

    getFilteredLancamentosByDateRange = () => {
        return this.state.lancamentos.filter((item) => {
            const dateToCheck = this.state.referenciaById[item.id_lancamento] || item.referencia;
            return this.isDateInRange(dateToCheck);
        });
    };

    saveEditReference = () => {
        const { editLancamento, editReferenciaDraft } = this.state;

        if (!editLancamento) {
            this.closeEditReference();
            return;
        }

        const idLancamento = editLancamento.id_lancamento;
        const nextDate = this.normalizeDate(editReferenciaDraft);

        if (!nextDate) {
            toast.error('Informe uma data de referência válida.');
            return;
        }

        this.setState({ loading: true });
        AppState.isLoading = true;

        this.persistReferencia(idLancamento, nextDate)
            .then(() => {
                this.closeEditReference();
                toast.error('Nova referência salva com sucesso!');
                this.getLancamentos();
            })
            .catch((error) => {
                const status = String(error.status || error.message || '');

                if (status === '401') {
                    window.location.reload();
                    return;
                }

                const backendMessage = this.getBackendErrorMessage(
                    error,
                    'Não foi possível salvar a nova referência.'
                );
                toast.error(`Erro ao salvar referência.\nDetalhe: ${backendMessage}`);
            })
            .finally(() => {
                this.setState({ loading: false });
                AppState.isLoading = false;
            });
    };

    getSelectedRows = () => {
        return this.state.lancamentos.filter(
            (item) => this.state.selectedById[item.id_lancamento] === true
        );
    };

    getFilteredAvailableRows = () => {
        const fallbackRows = this.state.lancamentos;

        if (!this.availableTable || typeof this.availableTable.getResolvedState !== 'function') {
            return fallbackRows;
        }

        const resolvedState = this.availableTable.getResolvedState();
        if (!resolvedState || !Array.isArray(resolvedState.sortedData)) {
            return fallbackRows;
        }

        const hasActiveFilters =
            Array.isArray(resolvedState.filtered) &&
            resolvedState.filtered.some((filter) => String(filter.value || '').trim() !== '');

        if (resolvedState.sortedData.length === 0 && !hasActiveFilters) {
            return fallbackRows;
        }

        return resolvedState.sortedData
            .map((row) => (row && row._original ? row._original : row))
            .filter(Boolean);
    };

    getCurrentMonthRows = () => {
        return this.state.bloqueados;
    };

    isAllSelectableChecked = () => {
        const selectableRows = this.getFilteredAvailableRows();
        if (selectableRows.length === 0) return false;

        return selectableRows.every(
            (item) => this.state.selectedById[item.id_lancamento] === true
        );
    };

    toggleSelectAllAvailable = (event) => {
        const checked = event.target.checked;
        const filteredRows = this.getFilteredAvailableRows();

        this.setState((prevState) => {
            const nextSelectedById = { ...prevState.selectedById };

            filteredRows.forEach((item) => {
                nextSelectedById[item.id_lancamento] = checked;
            });

            return { selectedById: nextSelectedById };
        });
    };

    getSelectionMessage = () => {
        const selectedRows = this.getSelectedRows();
        if (selectedRows.length === 0) return '';

        const grouped = {};

        selectedRows.forEach((row) => {
            const referencia = this.state.referenciaById[row.id_lancamento] || row.referencia;
            const monthKey = this.toMonthKey(referencia);
            const safeMonthKey = monthKey || 'sem-data';
            grouped[safeMonthKey] = (grouped[safeMonthKey] || 0) + 1;
        });

        const parts = Object.keys(grouped)
            .sort()
            .map((monthKey) => {
                const quantity = grouped[monthKey];
                const label = monthKey === 'sem-data' ? 'não informada' : this.getMonthLabel(monthKey);
                return `${quantity} lançamento${quantity > 1 ? 's' : ''} com referência no mês ${label}`;
            });

        if (parts.length === 1) {
            return `Você selecionou ${parts[0]}.`;
        }

        return `Você selecionou ${parts.join(' e ')}.`;
    };

    openConfirmDialog = () => {
        const selectedRows = this.getSelectedRows();
        if (selectedRows.length === 0) {
            toast.error('Selecione ao menos um lançamento para confirmar o rateio.');
            return;
        }

        this.setState({ confirmDialogOpen: true });
    };

    closeConfirmDialog = () => {
        this.setState({ confirmDialogOpen: false });
    };

    persistReferencia = (idLancamento, referencia) => {
        return fetch(`${AppState.serverAddr}/${EDIT_REFERENCIA_ENDPOINT}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json; charset=utf-8',
                Authorization: AppState.token,
            },
            body: JSON.stringify({
                id_lancamento: idLancamento,
                referencia,
            }),
        }).then((response) => {
            if (!response.ok) {
                return this.parseErrorResponse(response);
            }

            return response.json();
        });
    };

    confirmRateio = () => {
        const selectedRows = this.getSelectedRows();
        if (selectedRows.length === 0) {
            this.closeConfirmDialog();
            return;
        }

        const editedRows = selectedRows
            .map((item) => {
                const referenciaAtualizada =
                    this.state.referenciaById[item.id_lancamento] || item.referencia;

                return {
                    id_lancamento: item.id_lancamento,
                    referenciaAtualizada,
                    editado:
                        this.normalizeDate(item.referencia) !==
                        this.normalizeDate(referenciaAtualizada),
                };
            })
            .filter((item) => item.editado);

        const payload = {
            ids: selectedRows.map((item) => Number(item.id_lancamento)),
        };

        AppState.isLoading = true;
        this.setState({ loading: true });

        Promise.all(
            editedRows.map((item) =>
                this.persistReferencia(item.id_lancamento, item.referenciaAtualizada)
            )
        )
            .then(() =>
                fetch(`${AppState.serverAddr}/${CONFIRM_RATEIO_ENDPOINT}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json; charset=utf-8',
                        Authorization: AppState.token,
                    },
                    body: JSON.stringify(payload),
                })
            )
            .then((response) => {
                if (!response.ok) {
                    return this.parseErrorResponse(response);
                }

                // Alguns backends retornam 200 sem body JSON; evita cair no catch por parse.
                return response.text();
            })
            .then((rawBody) => {
                if (!rawBody) {
                    return null;
                }

                try {
                    return JSON.parse(rawBody);
                } catch (e) {
                    return rawBody;
                }
            })
            .then(() => {
                toast.error('Confirmação de rateio realizada com sucesso.');
                this.closeConfirmDialog();
                this.getLancamentos();

            })
            .catch((error) => {
                this.closeConfirmDialog();
                const status = String(error.status || error.message || '');

                if (status === '401') {
                    window.location.reload();
                    return;
                }

                const backendMessage = this.getBackendErrorMessage(
                    error,
                    'Verifique os dados e tente novamente.'
                );

                toast.error(`Erro ao confirmar rateio.\nDetalhe: ${backendMessage}`);
            })
            .finally(() => {
                AppState.isLoading = false;
                this.setState({ loading: false });
            });
    };

    getColumns = () => {
        const cellStyle = {
        height: '22px',
        textAlign: 'center',
        verticalAlign: 'middle',
        position: 'relative',
        top: '15%',
    };

    return [
        {
            Header: 'ID Lançamento',
            accessor: 'id_lancamento',
            width: 120,
            filterable: false,
            sortMethod: (a, b) => this.toNumber(a) - this.toNumber(b),
            Cell: ({ value }) => <div style={cellStyle}>{value}</div>
        },
        {
            Header: 'Descrição',
            accessor: 'descricao',
            filterable: true,
            Cell: ({ value }) => <div style={cellStyle}>{value}</div>
        },
        {
            Header: 'Conta',
            accessor: 'id_conta',
            width: 120,
            filterable: true,
            Cell: ({ value }) => <div style={cellStyle}>{value}</div>
        },
        {
            Header: 'Data Pagamento',
            accessor: 'dt_pag_recb',
            width: 140,
            filterable: false,
            Cell: ({ original }) => (
                <div style={cellStyle}>
                    {Utils.dateToPT(
                        original.dt_confirmacao ||
                        original.dt_pag_recb ||
                        original.timestamp_criacao ||
                        ''
                    )}
                </div>
            )
        },
        {
            Header: 'CPF Usuário',
            accessor: 'cpf_pagador',
            width: 150,
            filterable: false,
            Cell: ({ original }) => (
                <div style={cellStyle}>
                    {original.cpf_usuario || original.cpf_pagador || original.cpf_cnpj_contraparte || '-'}
                </div>
            )
        },
        {
            Header: 'Data Referência',
            accessor: 'referencia',
            width: 220,
            filterable: false,
            Cell: ({ original }) => {
                const idLancamento = original.id_lancamento;
                const currentValue = this.state.referenciaById[idLancamento] || original.referencia;

                return (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', height: '100%' }}>
                        <span style={{ lineHeight: '1' }}>{Utils.dateToPT(currentValue || '')}</span>
                        <IconButton
                            onClick={() => this.openEditReference(idLancamento)}
                            title="Editar referência"
                            style={{ padding: '4px', marginLeft: '4px' }}
                        >
                            <EditIcon fontSize='1.1rem' />
                        </IconButton>
                    </div>
                );
            },
        },
        {
            Header: 'Status Rateio',
            accessor: 'status_rateio',
            width: 150,
            filterable: false,
            Cell: ({ value }) => {
                let label = '-';
                if (value) {
                    const normalized = String(value).trim().toLowerCase();
                    if (normalized === 'pendente') label = 'Pendente';
                    else if (normalized === 'em_andamento') label = 'Em andamento';
                    else if (normalized === 'concluido' || normalized === 'concluído') label = 'Concluído';
                    else label = value;
                }
                return <div style={cellStyle}>{label}</div>;
            },
        },
        {
            Header: 'Valor',
            accessor: 'valor',
            width: 120,
            filterable: true,
            sortMethod: (a, b) => this.toNumber(a) - this.toNumber(b),
            Footer: (info) => {
                // Garante que pegamos a lista de linhas (aceita tanto data quanto info.data)
                const rows = Array.isArray(info) ? info : (info.data || info.rows || []);
                // Faz o reduce extraindo o valor do objeto original (item._original) ou direto (item)
                const total = rows.reduce((sum, item) => {
                    const rawValue = item?._original ? item._original.valor : item?.valor;
                    const numValue = typeof rawValue === 'number' 
                        ? rawValue 
                        : parseFloat(String(rawValue || 0).replace(',', '.'));

                    return sum + (isNaN(numValue) ? 0 : numValue);
                }, 0);

                return <div style={cellStyle}>{`R$ ${Utils.float2moeda(total)}`}</div>;
            },
            Cell: ({ value }) => <div style={cellStyle}>{`R$ ${Utils.float2moeda(parseFloat(value || 0))}`}</div>,
        },
        {
            Header: 'Selecionar',
            accessor: 'id_lancamento',
            width: 110,
            filterable: false,
            Cell: ({ original }) => (
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    width: '100%' 
                }}>
                    <Checkbox
                        color="primary"
                        checked={!!this.state.selectedById[original.id_lancamento]}
                        onChange={this.handleSelect(original.id_lancamento)}
                    />
                </div>
            ),
        },
    ];
};

    getBlockedColumns = () => {
    const cellStyle = { height: '22px', textAlign: 'center', verticalAlign: 'middle', position: 'relative', top: '15%' };
    const baseColumns = this.getColumns().filter(
        (column) => column.accessor !== 'id_lancamento' || column.Header !== 'Selecionar'
    );

        return [
            ...baseColumns,
            {
                Header: 'Motivo do bloqueio',
                accessor: 'motivo_bloqueio',
                width: 260,
                Cell: ({ value }) => {
                    let motivo = value || '-';
                    if (value === 'referencia_vazia') motivo = 'Referência vazia';
                    else if (value === 'referencia_formato_invalido') motivo = 'Formato de referência inválido';
                    else if (value === 'referencia_mes_atual_ou_futuro') motivo = 'Referência no mês atual ou futuro';
                    
                    return <div style={cellStyle}>{motivo}</div>;
                },
            },
        ];
    };

    render() {
    const lancamentosFiltrados = this.getFilteredLancamentosByDateRange();
    const availableRows = lancamentosFiltrados;
    const filteredAvailableRows = this.getFilteredAvailableRows();
    const currentMonthRows = this.getCurrentMonthRows();
    const selectedCount = this.getSelectedRows().length;
    const selectableCount = filteredAvailableRows.length;
    const allSelectableChecked = this.isAllSelectableChecked();
    const selectionMessage = this.getSelectionMessage();
    const canConfirm = selectedCount > 0 && !this.state.loading;

    return (
        <div id="ConfirmRateio" style={{ marginTop: '5em' }}>
            
            <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '16px' 
            }}>
                <h1 style={{ margin: 0 }}>Gerenciamento de Rateio</h1>
                
                <DsButton
                    variant="contained"
                    onClick={this.openConfirmDialog}
                    disabled={!canConfirm}
                    style={{
                        backgroundColor: canConfirm ? '#2e7d32' : '#e0e0e0',
                        color: canConfirm ? '#ffffff' : 'rgba(0, 0, 0, 0.38)',
                    }}
                >
                    Confirmar rateio
                </DsButton>
            </div>

            <div style={{
                marginBottom: '16px',
                display: 'flex',
                gap: '16px',
                alignItems: 'flex-end',
                flexWrap: 'wrap',
            }}>
                <TextField
                    label="Data de Referência (início)"
                    type="date"
                    value={this.state.dataInicio || ''}
                    onChange={this.handleDataInicioChange}
                    InputLabelProps={{ shrink: true }}
                    style={{ minWidth: '200px' }}
                />
                <TextField
                    label="Data de Referência (fim)"
                    type="date"
                    value={this.state.dataFim || ''}
                    onChange={this.handleDataFimChange}
                    InputLabelProps={{ shrink: true }}
                    style={{ minWidth: '200px' }}
                />
                {(this.state.dataInicio || this.state.dataFim) && (
                    <DsButton
                        variant="contained"
                        size="small"
                        onClick={() => this.setState({ dataInicio: '', dataFim: '' })}
                        style={{
                            backgroundColor: '#2e7d32',
                            color: '#ffffff',
                        }}
                    >
                        Limpar filtro de data
                    </DsButton>
                )}
            </div>

            <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'flex-end', 
                marginBottom: '8px' 
            }}>
                <h3 style={{ margin: 0 }}>
                    Disponíveis para rateio ({availableRows.length})
                </h3>

                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '8px' }}>
                        Selecionar todos os pagamentos disponíveis
                    </span>
                    <div style={{ width: '110px', display: 'flex', justifyContent: 'center' }}>
                        <Checkbox
                            color="primary"
                            checked={allSelectableChecked}
                            onChange={this.toggleSelectAllAvailable}
                            disabled={selectableCount === 0}
                        />
                    </div>
                </div>
            </div>

            {selectionMessage ? (
                <div style={{
                    marginBottom: '12px',
                    padding: '10px 12px',
                    borderRadius: '4px',
                    background: '#eef7ff',
                    border: '1px solid #cde4ff',
                }}>
                    {selectionMessage}
                </div>
            ) : null}

            <DataTable
                filterable={true}
                data={availableRows}
                columns={this.getColumns()}
                defaultPageSize={10}
                ref={(r) => {
                    this.availableTable = r;
                }}
                filterMethod={(filter, row) => {
                    const normalize = (value) =>
                        String(value || '')
                            .toLowerCase()
                            .normalize('NFD')
                            .replace(/[\u0300-\u036f]/g, '');

                    const filterValue = normalize(filter.value);

                    if (filter.id === 'dt_pag_recb' || filter.id === 'referencia') {
                        return normalize(Utils.dateToPT(String(row[filter.id] || ''))).includes(filterValue);
                    }

                    return normalize(row[filter.id]).includes(filterValue);
                }}
                getTheadThProps={() => ({
                    style: { textAlign: 'center' },
                })}
                getTdProps={() => ({
                    style: { textAlign: 'center' },
                })}
                noDataText={
                    this.state.loading
                        ? 'Carregando lançamentos...'
                        : 'Não há lançamentos disponíveis para confirmação de rateio.'
                }
            />

            <h3 style={{ marginTop: '40px', marginBottom: '8px' }}>
                Lançamentos bloqueados para confirmação ({currentMonthRows.length})
            </h3>

            <DataTable
                data={currentMonthRows}
                columns={this.getBlockedColumns()}
                defaultPageSize={10}
                getTheadThProps={() => ({
                    style: { textAlign: 'center' },
                })}
                getTdProps={() => ({
                    style: { textAlign: 'center' },
                })}
                noDataText={
                    this.state.loading
                        ? 'Carregando lançamentos...'
                        : 'Não há lançamentos bloqueados para confirmação.'
                }
            />

            <Dialog
                open={this.state.confirmDialogOpen}
                onClose={this.closeConfirmDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Confirmar rateio</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Tem certeza que deseja continuar com a confirmação dos lançamentos selecionados?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <DsButton onClick={this.closeConfirmDialog} color="default">
                        Cancelar
                    </DsButton>
                    <DsButton
                        onClick={this.confirmRateio}
                        color="default"
                        variant="contained"
                        style={{ backgroundColor: '#2e7d32', color: '#ffffff' }}
                    >
                        Sim
                    </DsButton>
                </DialogActions>
            </Dialog>

            <Dialog
                open={this.state.editDialogOpen}
                onClose={this.closeEditReference}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Editar referência do lançamento</DialogTitle>
                <DialogContent>
                    <Grid container spacing={16}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="ID Lançamento"
                                value={this.state.editLancamento ? this.state.editLancamento.id_lancamento : ''}
                                fullWidth
                                disabled
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Conta"
                                value={this.state.editLancamento ? this.state.editLancamento.id_conta : ''}
                                fullWidth
                                disabled
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Data Pagamento"
                                value={
                                    this.state.editLancamento
                                        ? Utils.dateToPT(
                                            this.state.editLancamento.dt_confirmacao ||
                                            this.state.editLancamento.dt_pag_recb ||
                                            this.state.editLancamento.timestamp_criacao ||
                                            ''
                                        )
                                        : ''
                                }
                                fullWidth
                                disabled
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Descrição"
                                value={this.state.editLancamento ? this.state.editLancamento.descricao : ''}
                                fullWidth
                                disabled
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="CPF Usuário"
                                value={
                                    this.state.editLancamento
                                        ? (
                                            this.state.editLancamento.cpf_usuario ||
                                            this.state.editLancamento.cpf_pagador ||
                                            this.state.editLancamento.cpf_cnpj_contraparte ||
                                            '-'
                                        )
                                        : ''
                                }
                                fullWidth
                                disabled
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Valor"
                                value={
                                    this.state.editLancamento
                                        ? `R$ ${Utils.float2moeda(parseFloat(this.state.editLancamento.valor || 0))}`
                                        : ''
                                }
                                fullWidth
                                disabled
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Data Referência"
                                type="date"
                                value={this.state.editReferenciaDraft || ''}
                                onChange={this.handleEditReferenceDraftChange}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <DsButton onClick={this.closeEditReference} color="default">
                        Cancelar
                    </DsButton>
                    <IconButton
                        onClick={this.saveEditReference}
                        title="Salvar nova referência"
                        style={{ color: '#2e7d32' }}
                    >
                        <SaveIcon />
                    </IconButton>
                </DialogActions>
            </Dialog>
        </div> 
    );
    }
}

export default ConfirmRateio;
